import Decimal from "decimal.js";
import { orderBy, range } from "lodash";

import { AssetClassId } from "@fartherfinance/frontend/api/Investment/types";
import {
  PortfolioPlanId,
  SecurityId,
  Ticker,
} from "@fartherfinance/frontend/api/Types";

import getAssetClassCategoryEnumFromLabel from "../helpers/getAssetClassCategoryEnumFromLabel";
import { assertUnreachable } from "@src/utils/assertUnreachable";

import {
  ADD_ASSET_CLASS_TO_MODEL,
  ADD_CUSTOM_ASSET_CLASS_TO_LIST,
  ADD_SECURITY_TO_ASSET_CLASS_IN_MODEL,
  ADD_SLEEVE_TO_ASSET_CLASS_IN_MODEL,
  EXPAND_COLLAPSE_CHILDREN,
  REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL,
  REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL,
  SET_ALL_LOCKED,
  SET_DEFAULT_ASSET_CLASSES_AND_THEIR_CATEGORIES,
  SET_LOCKED,
  SET_MODEL_DETAILS,
  SET_SLEEVE_NAME,
  SET_SLEEVES_AND_SECURITIES,
  SET_TABLE,
  SET_WEIGHT,
  SORT_ASSET_CLASS_WEIGHTS_DESC,
  SORT_ASSET_CLASSES_ASC,
  UPDATE_CUSTOM_ASSET_CLASS,
} from "./actions";
import {
  ActionPayload,
  AssetClass,
  AssetClassAllocationsObject,
  AssetClassCategory,
  Children,
  ElementID,
  KeyValueToUpdate,
  ModelTableRowType,
  PackagedSleevesAndSecurities,
  ReducerState,
  Security,
  Sleeve,
  SleeveOrSecurity,
} from "./Types";

const generateId = (idLength: number) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

  return range(idLength)
    .map(() => alphabet[Math.floor(Math.random() * alphabet.length)])
    .join("") as ElementID;
};

export const generateElementId = () => generateId(8);

export const generatePortfolioPlanId = () => {
  return `AIP_FAR_${generateId(8)}-${generateId(4)}-${generateId(
    4
  )}-${generateId(4)}-${generateId(12)}`;
};

const UI_GENERATED_CUSTOM_ASSET_CLASS_ID_PREFIX = "__customAssetClass__";

// we need to generate a temporary id for custom asset classes in the asset class dropdown (to uniquely id a dropdown item) - this id will NOT be sent to the BE, only the name and category will
export const generateCustomAssetClassId = () =>
  `${UI_GENERATED_CUSTOM_ASSET_CLASS_ID_PREFIX}${generateId(
    8
  )}` as AssetClassId;

// UAT and Prod securityIds for CASH | US_DOLLARS
const CASH_US_DOLLARS_SecurityId_PROD =
  "3aa41509-0f93-4c6b-9d03-8d4df2b7b0f2" as SecurityId;

const CASH_US_DOLLARS_SecurityId_UAT =
  "9d0bbed0-1499-4bcf-bce3-3e36c476ba7f" as SecurityId;

const CASH_US_DOLLARS_SecurityId =
  process.env.WEBAPP_ENV === "PROD"
    ? CASH_US_DOLLARS_SecurityId_PROD
    : CASH_US_DOLLARS_SecurityId_UAT;

export const NO_ASSET_CLASS_NAME = " No asset class"; // the white space (first char) is important bc it makes sure this invisible asset class is always sorted alphabetically first (based on ascii table)

export const NO_ASSET_CLASS_ASSET_CLASS: AssetClass = {
  id: generateElementId(),
  type: "AssetClass" as Extract<ModelTableRowType, "AssetClass">,
  isInvisible: true, // used to hide the asset class from the UI - all children: (Sleeve | Security) elements need an AssetClass as a parent, this is used to represent a loose security or sleeve with no asset class
  category: "Other",
  assetClassId: "__NOT_AN_ASSET_CLASS__" as AssetClassId,
  name: NO_ASSET_CLASS_NAME,
  weight: "0.00",
  isLocked: false,
  expand: true,
  children: [],
};

const CASH_US_DOLLARS_SECURITY: Security = {
  id: generateElementId(),
  type: "Security" as Extract<ModelTableRowType, "Security">,
  ticker: "US DOLLARS" as Ticker,
  securityId: CASH_US_DOLLARS_SecurityId,
  description: "Cash",
  weight: "100.00",
  isLocked: true,
};

export const DEFAULT_MODEL_TABLE: AssetClass[] = [
  {
    ...NO_ASSET_CLASS_ASSET_CLASS,
    weight: CASH_US_DOLLARS_SECURITY.weight,
    children: [CASH_US_DOLLARS_SECURITY],
  },
];

export const areAnyWeightsZero = (modelTable: AssetClass[]) =>
  modelTable.some((ac) =>
    ac.children.some((child) => {
      const weight = parseFloat(child.weight);
      return weight === 0 || isNaN(weight);
    })
  );

export const hasLooseSecuritiesOrSleeves = (modelTable: AssetClass[]) => {
  const invisibleNoAssetClass = modelTable.find((ac) => ac.isInvisible);

  return (invisibleNoAssetClass?.children ?? []).length > 0;
};

const orderChildrenByNameAsc = (children: Children): Children =>
  orderBy(
    children.map((child) => {
      switch (child.type) {
        case "Sleeve":
          const { children, ...rest } = child;

          return {
            ...rest,
            children: orderChildrenByNameAsc(child.children),
          };
        case "Security":
          return {
            ...child,
          };
        default:
          return assertUnreachable(child);
      }
    }),
    [(c) => (c.type === "Sleeve" ? c.name : c.ticker)],
    ["asc"]
  );

const orderChildrenByWeightDesc = (children: Children): Children =>
  orderBy(
    children.map((child) => {
      switch (child.type) {
        case "Sleeve":
          const { children, ...rest } = child;

          return {
            ...rest,
            children: orderChildrenByWeightDesc(child.children),
          };
        case "Security":
          return {
            ...child,
          };
        default:
          return assertUnreachable(child);
      }
    }),
    [(c) => parseFloat(c.weight)],
    ["desc"]
  );

const areAnyChildrenExpanded = (
  elementId: ElementID | null,
  children: Children
): boolean => {
  return children.some((child) => {
    const isTarget = elementId === child.id || elementId === null;
    if (isTarget === false) {
      return false;
    }

    switch (child.type) {
      case "Sleeve":
        return (
          child.expand || areAnyChildrenExpanded(elementId, child.children)
        );
      case "Security":
        return false;
      default:
        return assertUnreachable(child);
    }
  });
};

// find the target element and check if it or any of its children are expanded
// if elementId is null, we are looking at every child - only the top row passes null
export const areAnyElementsExpanded = (
  elementId: ElementID | null,
  modelTable: AssetClass[]
) =>
  modelTable.some((assetClass) => {
    const isTarget = elementId === assetClass.id || elementId === null;
    if (isTarget === false) {
      return false;
    }

    if (assetClass.isInvisible) {
      return areAnyChildrenExpanded(elementId, assetClass.children);
    }

    return (
      assetClass.expand ||
      areAnyChildrenExpanded(elementId, assetClass.children)
    );
  });

// Not recursive - will only look at 1 level (directly) under an asset class
const getFirstLevelSecuritiesAndSleevesInChildren = (
  assetClass: AssetClass
): PackagedSleevesAndSecurities => {
  return assetClass.children.reduce((list, child) => {
    const isCustom = assetClass.assetClassId.startsWith(
      UI_GENERATED_CUSTOM_ASSET_CLASS_ID_PREFIX
    );

    const assetClassObj = isCustom
      ? {
          assetClassName: assetClass.name,
          assetClassCategory: getAssetClassCategoryEnumFromLabel(
            assetClass.category
          ),
        }
      : { assetClassId: assetClass.assetClassId };

    switch (child.type) {
      case "Sleeve":
        return [
          ...list,
          {
            type: "NestedPlan",
            planId: child.portfolioPlanId,
            weight: new Decimal(child.weight).div(100),
            ...(assetClass.isInvisible === false && assetClassObj),
          },
        ];
      case "Security":
        return [
          ...list,
          {
            type: "Security",
            securityId: child.securityId,
            weight: new Decimal(child.weight).div(100),
            ...(assetClass.isInvisible === false && assetClassObj),
          },
        ];
      default:
        return assertUnreachable(child);
    }
  }, [] as PackagedSleevesAndSecurities);
};

// does not return securities nested/inside of sleeves - only the top level securities outside of sleeves = directly under asset classes
export const getFirstLevelSecuritiesAndSleeves = (
  modelTable: AssetClass[],
  ignoreElementsUnderAnAssetClass: boolean = false // only gets loose securities and sleeves
): PackagedSleevesAndSecurities => {
  // if ignoreElementsUnderAnAssetClass is true, we only want to return the securities and sleeves that are directly under the invisible ("No asset class") asset class = loose securities and sleeves
  if (ignoreElementsUnderAnAssetClass) {
    return modelTable
      .filter((ac) => ac.children.length > 0) // only send asset classes with children
      .filter((ac) => ac.isInvisible === true) // only look at elements under the invisible asset class
      .reduce(
        (list, assetClass) => [
          ...list,
          ...getFirstLevelSecuritiesAndSleevesInChildren(assetClass),
        ],
        [] as PackagedSleevesAndSecurities
      );
  }

  return modelTable
    .filter((ac) => ac.children.length > 0) // only send asset classes with children
    .reduce(
      (list, assetClass) => [
        ...list,
        ...getFirstLevelSecuritiesAndSleevesInChildren(assetClass),
      ],
      [] as PackagedSleevesAndSecurities
    );
};

const sleeveInChildren = (
  portfolioPlanId: PortfolioPlanId,
  children: Children
): boolean =>
  children.some((child) => {
    switch (child.type) {
      case "Sleeve": {
        if (child.portfolioPlanId === portfolioPlanId) {
          return true;
        }

        return sleeveInChildren(portfolioPlanId, child.children);
      }
      case "Security":
        return false;
      default:
        return assertUnreachable(child);
    }
  });

export const isSleeveAlreadyInModel = (
  portfolioPlanId: PortfolioPlanId,
  modelTable: AssetClass[]
): boolean => {
  return modelTable.some((assetClass) =>
    sleeveInChildren(portfolioPlanId, assetClass.children)
  );
};

const addSleevesAndSecuritiesToChildInChildren = (
  targetId: ElementID,
  sleeves: Sleeve[],
  securities: Security[],
  children: Children
): Children =>
  children.map((child) => {
    switch (child.type) {
      case "Sleeve": {
        if (targetId === child.id && child.children.length > 0) {
          throw new Error(
            "Cannot add children to a sleeve that already has children"
          );
        }

        const { children, ...rest } = child;

        return {
          ...rest,
          children:
            targetId === child.id
              ? [...sleeves, ...securities] // a sleeve will only addSleevesAndSecuritiesToChildInChildren
              : addSleevesAndSecuritiesToChildInChildren(
                  targetId,
                  sleeves,
                  securities,
                  child.children
                ),
        };
      }
      case "Security":
        return {
          ...child,
        };
      default:
        return assertUnreachable(child);
    }
  });

const deepCopyChild = (child: Sleeve | Security): Sleeve | Security => {
  switch (child.type) {
    case "Sleeve":
      const { children, ...rest } = child;

      return {
        ...rest,
        children: deepCopyChildren(child.children),
      };
    case "Security":
      return {
        ...child,
      };
    default:
      return assertUnreachable(child);
  }
};

const deepCopyChildren = (children: Children): Children =>
  children.map((child) => deepCopyChild(child));

const getSleeveOrSecurityByIdFromChildren = (
  targetId: ElementID,
  children: Children
): Sleeve | Security | undefined =>
  children.reduce<Sleeve | Security | undefined>((result, child) => {
    if (child.id === targetId) {
      return deepCopyChild(child);
    }

    switch (child.type) {
      case "Sleeve":
        return getSleeveOrSecurityByIdFromChildren(targetId, child.children);
      case "Security":
        return result; // could be undefined OR an already found Sleeve or Security
      default:
        return assertUnreachable(child);
    }
  }, undefined);

const getSleeveSecurityOrAssetClassById = (
  targetId: ElementID,
  modelTable: AssetClass[]
): Sleeve | Security | AssetClass | undefined =>
  modelTable.reduce<Sleeve | Security | AssetClass | undefined>(
    (result, assetClass) => {
      if (targetId === assetClass.id) {
        return assetClass;
      }

      const targetInChildren = getSleeveOrSecurityByIdFromChildren(
        targetId,
        assetClass.children
      );

      if (targetInChildren === undefined) {
        return result; // could be undefined OR an already found Sleeve or Security
      }

      return targetInChildren;
    },
    undefined
  );

const deepCopyAndRemoveChildById = (
  targetId: ElementID,
  children: Children
): Children => {
  return children.reduce((entries, child) => {
    if (targetId === child.id) {
      return entries;
    }

    switch (child.type) {
      case "Sleeve":
        const { children, ...rest } = child;

        return [
          ...entries,
          {
            ...rest,
            children: deepCopyAndRemoveChildById(targetId, child.children),
          },
        ];
      case "Security":
        return [
          ...entries,
          {
            ...child,
          },
        ];
      default:
        return assertUnreachable(child);
    }
  }, [] as Children);
};

const deepCopyAndRemoveElementById = (
  targetId: ElementID,
  modelTable: AssetClass[]
) =>
  modelTable.reduce((table, assetClass) => {
    if (targetId === assetClass.id) {
      return table;
    }

    const { children, ...rest } = assetClass;

    return [
      ...table,
      {
        ...rest,
        children: deepCopyAndRemoveChildById(targetId, assetClass.children),
      },
    ];
  }, [] as AssetClass[]);

const deepCopyAndUpdateSpecificElement = (
  targetId: ElementID,
  keyValue: KeyValueToUpdate,
  children: Children
): Children => {
  const [key, value] = keyValue;

  return children.map((child) => {
    switch (child.type) {
      case "Sleeve":
        const { children, ...rest } = child;

        return {
          ...rest,
          ...(child.id === targetId && { [key]: value }),
          children: deepCopyAndUpdateSpecificElement(
            targetId,
            keyValue,
            child.children
          ),
        };
      case "Security":
        // don't add "name" | "expand" keys to Security
        if (key === "weight" || key === "isLocked") {
          return {
            ...child,
            ...(child.id === targetId && { [key]: value }),
          };
        }

        return {
          ...child,
        };
      default:
        return assertUnreachable(child);
    }
  });
};

// apply the update to the target element and all its children
// if elementId is null, the update is applied to every element (apply is set to true from the beginning) - only the top row passes null
const deepCopyAndUpdateElementKey = (
  elementId: ElementID | null,
  keyValue: KeyValueToUpdate,
  apply: boolean,
  children: Children
): Children => {
  const [key, value] = keyValue;

  return children.map((child) => {
    let applyUpdate = apply;
    if (elementId === child.id) {
      applyUpdate = true;
    }

    switch (child.type) {
      case "Sleeve":
        const { children, ...rest } = child;

        return {
          ...rest,
          ...(applyUpdate && { [key]: value }),
          children: deepCopyAndUpdateElementKey(
            elementId,
            keyValue,
            applyUpdate,
            child.children
          ),
        };
      case "Security":
        // don't add "name" | "expand" keys to Security
        if (key === "weight" || key === "isLocked") {
          return {
            ...child,
            ...(applyUpdate && { [key]: value }),
          };
        }

        return {
          ...child,
        };
      default:
        return assertUnreachable(child);
    }
  });
};

const areAllChildrenLocked = (children: Children): boolean => {
  return children.every((child) => {
    switch (child.type) {
      case "Sleeve": {
        if (child.isLocked === false) {
          return false;
        }

        return areAllChildrenLocked(child.children);
      }
      case "Security":
        return child.isLocked;
      default:
        return assertUnreachable(child);
    }
  });
};

export const areAllLocked = (modelTable: AssetClass[]) => {
  return modelTable.every((assetClass) => {
    if (assetClass.isLocked === false) {
      return false;
    }

    return areAllChildrenLocked(assetClass.children);
  });
};

const getParsedWeightOrThrow = (
  child: AssetClass | Sleeve | Security
): number => {
  const weight = child.weight === "" ? 0 : parseFloat(child.weight);
  if (isNaN(weight)) {
    const elementName = child.type === "Security" ? child.ticker : child.name;
    throw new Error(
      `Weight for ${child.type}: ${elementName}, is not a number!`
    );
  }

  return weight;
};

const getTotalWeightOfChildren = (children: Children): Decimal =>
  children.reduce((sum, child) => {
    const weight = getParsedWeightOrThrow(child);
    return sum.add(new Decimal(weight));
  }, new Decimal(0));

export const getTotalWeight = (modelTable: AssetClass[]) =>
  modelTable.reduce((sum, assetClass) => {
    const weight = getParsedWeightOrThrow(assetClass);

    if (assetClass.children.length > 0) {
      return sum.add(getTotalWeightOfChildren(assetClass.children)); // don't incorporate asset class weight if it has children - sum child weights instead
    }

    return sum.add(new Decimal(weight));
  }, new Decimal(0));

export const getTotalWeightOfNonAssetClasses = (modelTable: AssetClass[]) =>
  modelTable.reduce((sum, assetClass) => {
    if (assetClass.children.length > 0) {
      return sum.add(getTotalWeightOfChildren(assetClass.children)); // don't incorporate asset class weight if it has children - sum child weights instead
    }

    return sum;
  }, new Decimal(0));

export const getAssetClassAllocations = (
  modelTable: AssetClass[]
): AssetClassAllocationsObject =>
  modelTable.reduce(
    (allocations, assetClass) => {
      if (assetClass.isInvisible) {
        // skip these as they are not being classified by the user - we will hit FinMason to get the allocations for these
        return allocations;
      }

      const weight = getTotalWeightOfChildren(assetClass.children);

      return {
        ...allocations,
        [assetClass.category]:
          allocations[assetClass.category] + weight.toNumber(),
      };
    },
    {
      Equity: 0,
      "Fixed Income": 0,
      "Cash Equivalents": 0,
      Other: 0,
    } as AssetClassAllocationsObject
  );

// returns a hash of the asset class weights - used to determine if the weights have changed across the asset classes
export const computeAssetClassWeightsHash = (modelTable: AssetClass[]) =>
  modelTable.reduce((hash, assetClass) => {
    const weight = getTotalWeightOfChildren(assetClass.children).toNumber();

    if (weight === 0) {
      return hash; // don't include asset classes with no children in the hash - if an asset class (without weight) is added or removed from the model it wont change the hash - this is desired as we don't want to trigger stats and classifications if an asset class (without weight) is added or removed
    }

    return `${hash}_${assetClass.name}_${weight}`;
  }, "");

const getNumberOfSleevesOrSecuritiesInChildren = (
  elementType: SleeveOrSecurity,
  children: Children
): number =>
  children.reduce((sum, child) => {
    switch (child.type) {
      case "Sleeve": {
        const newSum = elementType === "Sleeve" ? sum + 1 : sum;

        return (
          newSum +
          getNumberOfSleevesOrSecuritiesInChildren(elementType, child.children)
        );
      }
      case "Security":
        return elementType === "Security" ? sum + 1 : sum;
      default:
        return assertUnreachable(child);
    }
  }, 0);

export const getNumberOfSleevesOrSecurities = (
  elementType: SleeveOrSecurity,
  modelTable: AssetClass[]
) =>
  modelTable.reduce((sum, assetClass) => {
    return (
      sum +
      getNumberOfSleevesOrSecuritiesInChildren(elementType, assetClass.children)
    );
  }, 0);

interface addAssetClassToModelParams {
  assetClassId: AssetClassId;
  assetClassName: string;
  category: AssetClassCategory;
  elementId: ElementID;
  modelTable: AssetClass[];
}

export const addAssetClassToModel = ({
  assetClassId,
  assetClassName,
  category,
  elementId,
  modelTable,
}: addAssetClassToModelParams) =>
  orderBy(
    [
      ...modelTable,
      {
        id: elementId,
        assetClassId: assetClassId,
        type: "AssetClass" as Extract<ModelTableRowType, "AssetClass">,
        name: assetClassName,
        category: category,
        weight: "0.00",
        isLocked: areAllLocked(modelTable),
        expand: true,
        isInvisible: assetClassName === NO_ASSET_CLASS_NAME,
        children: [],
      },
    ],
    [(a) => a.name],
    ["asc"]
  );

interface addSleeveToAssetClassInModelParams {
  name: string;
  portfolioPlanId: PortfolioPlanId;
  assetClassElementId: ElementID;
  weight?: string;
  modelTable: AssetClass[];
}

export const addSleeveToAssetClassInModel = ({
  name,
  portfolioPlanId,
  assetClassElementId,
  weight = "0.00",
  modelTable,
}: addSleeveToAssetClassInModelParams) => {
  const sleeve = {
    id: generateElementId(),
    type: "Sleeve" as Extract<ModelTableRowType, "Sleeve">,
    name,
    portfolioPlanId,
    weight,
    isLocked: areAllLocked(modelTable),
    expand: true,
    children: [],
  };

  return modelTable.map((assetClass) => {
    const { children, weight: assetClassWeight, ...rest } = assetClass;

    let newAssetClassWeight = assetClassWeight;
    if (assetClassElementId === assetClass.id) {
      const sum = new Decimal(assetClassWeight).add(new Decimal(weight));
      newAssetClassWeight = sum.toString();
    }

    return {
      ...rest,
      weight: newAssetClassWeight,
      children:
        assetClassElementId === assetClass.id
          ? orderBy(
              [...deepCopyChildren(assetClass.children), sleeve],
              [(c) => c.type],
              ["desc"] // sorts sleeves before securities
            )
          : deepCopyChildren(assetClass.children),
    };
  });
};

interface addSecurityToAssetClassInModelParams {
  ticker: Ticker;
  securityId: SecurityId;
  description: string;
  assetClassElementId: ElementID;
  weight?: string;
  modelTable: AssetClass[];
}

export const addSecurityToAssetClassInModel = ({
  ticker,
  securityId,
  description,
  assetClassElementId,
  weight = "0.00",
  modelTable,
}: addSecurityToAssetClassInModelParams) => {
  const security = {
    id: generateElementId(),
    type: "Security" as Extract<ModelTableRowType, "Security">,
    ticker,
    securityId,
    description,
    weight,
    isLocked: areAllLocked(modelTable),
  };

  return modelTable.map((assetClass) => {
    const { children, weight: assetClassWeight, ...rest } = assetClass;

    let newAssetClassWeight = assetClassWeight;
    if (assetClassElementId === assetClass.id) {
      const sum = new Decimal(assetClassWeight).add(new Decimal(weight));
      newAssetClassWeight = sum.toString();
    }

    return {
      ...rest,
      weight: newAssetClassWeight,
      children:
        assetClassElementId === assetClass.id
          ? orderBy(
              [...deepCopyChildren(assetClass.children), security],
              [(c) => c.type],
              ["desc"] // sorts sleeves before securities
            )
          : deepCopyChildren(assetClass.children),
    };
  });
};

function reducer(state: ReducerState, action: ActionPayload): ReducerState {
  const { type, payload } = action;

  switch (type) {
    case SET_MODEL_DETAILS: {
      return {
        ...state,
        ...payload,
      };
    }

    case SET_TABLE: {
      return {
        ...state,
        modelTable: payload,
      };
    }

    case SET_WEIGHT: {
      const { id: targetId, weight: incomingWeight, assetClassId } = payload;

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, weight, ...rest } = assetClass;

          const updatedChildren = deepCopyAndUpdateSpecificElement(
            targetId,
            ["weight", incomingWeight],
            children
          );

          let newWeight = weight;
          if (assetClassId === assetClass.assetClassId) {
            newWeight = getTotalWeightOfChildren(updatedChildren).toString();
          }

          return {
            ...rest,
            weight: assetClass.id === targetId ? incomingWeight : newWeight,
            children: updatedChildren,
          };
        }),
      };
    }

    case SET_LOCKED: {
      const { id: targetId, isLocked } = payload;

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, ...rest } = assetClass;

          return {
            ...rest,
            isLocked:
              assetClass.id === targetId ? isLocked : assetClass.isLocked,
            children: deepCopyAndUpdateSpecificElement(
              targetId,
              ["isLocked", isLocked],
              assetClass.children
            ),
          };
        }),
      };
    }

    case SET_ALL_LOCKED: {
      const { isLocked } = payload;

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, ...rest } = assetClass;

          return {
            ...rest,
            children: deepCopyAndUpdateElementKey(
              null,
              ["isLocked", isLocked],
              true,
              assetClass.children
            ),
          };
        }),
      };
    }

    case ADD_CUSTOM_ASSET_CLASS_TO_LIST: {
      const { assetClassId, assetClassName, category } = payload;

      const newOption = {
        id: assetClassId,
        label: assetClassName,
        label2: assetClassId.startsWith(
          UI_GENERATED_CUSTOM_ASSET_CLASS_ID_PREFIX
        )
          ? "(Custom)"
          : "",
      };

      return {
        ...state,
        assetCategoriesWithClasses: state.assetCategoriesWithClasses.map(
          (cat) => ({
            category: cat.category,
            options:
              cat.category === category
                ? orderBy(
                    [...cat.options, newOption],
                    [(o) => o.label],
                    ["asc"]
                  )
                : orderBy([...cat.options], [(o) => o.label], ["asc"]),
          })
        ),
      };
    }

    case UPDATE_CUSTOM_ASSET_CLASS: {
      const { currentCategoryAndClass, newCategoryAndClass } = payload;

      const newOption = {
        id: newCategoryAndClass.assetClassId,
        label: newCategoryAndClass.assetClassName,
        label2: newCategoryAndClass.assetClassId.startsWith(
          UI_GENERATED_CUSTOM_ASSET_CLASS_ID_PREFIX
        )
          ? "(Custom)"
          : "",
      };

      const removedAssetClassList = state.assetCategoriesWithClasses.map(
        (cat) => ({
          category: cat.category,
          options:
            cat.category === currentCategoryAndClass.category
              ? orderBy(
                  cat.options.filter(
                    (ac) => ac.id !== currentCategoryAndClass.assetClassId
                  ),
                  [(o) => o.label],
                  ["asc"]
                )
              : orderBy([...cat.options], [(o) => o.label], ["asc"]),
        })
      );

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, ...rest } = assetClass;

          const isTarget =
            assetClass.category === currentCategoryAndClass.category &&
            assetClass.name === currentCategoryAndClass.assetClassName;

          return {
            ...rest,
            name: isTarget
              ? newCategoryAndClass.assetClassName
              : assetClass.name,
            category: isTarget
              ? newCategoryAndClass.category
              : assetClass.category,
            children: deepCopyChildren(assetClass.children),
          };
        }),
        assetCategoriesWithClasses: removedAssetClassList.map((cat) => ({
          category: cat.category,
          options:
            cat.category === newCategoryAndClass.category
              ? orderBy([...cat.options, newOption], [(o) => o.label], ["asc"])
              : orderBy([...cat.options], [(o) => o.label], ["asc"]),
        })),
      };
    }

    case ADD_ASSET_CLASS_TO_MODEL: {
      const { assetClassId, assetClassName, category } = payload;

      return {
        ...state,
        modelTable: addAssetClassToModel({
          assetClassId,
          assetClassName,
          category,
          elementId: generateElementId(),
          modelTable: state.modelTable,
        }),
      };
    }

    case ADD_SLEEVE_TO_ASSET_CLASS_IN_MODEL: {
      const { name, portfolioPlanId, assetClassElementId, weight } = payload;

      return {
        ...state,
        modelTable: addSleeveToAssetClassInModel({
          name,
          portfolioPlanId,
          assetClassElementId,
          weight,
          modelTable: state.modelTable,
        }),
      };
    }

    case ADD_SECURITY_TO_ASSET_CLASS_IN_MODEL: {
      const { ticker, securityId, description, assetClassElementId, weight } =
        payload;

      return {
        ...state,
        modelTable: addSecurityToAssetClassInModel({
          ticker,
          securityId,
          description,
          assetClassElementId,
          weight,
          modelTable: state.modelTable,
        }),
      };
    }

    case REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL: {
      const { elementId, fromAssetClassId, toAssetClassElementId, weight } =
        payload;

      // we expect the elementId to be from a sleeve or security
      const sleeveOrSecurity = getSleeveSecurityOrAssetClassById(
        elementId,
        state.modelTable
      );
      if (sleeveOrSecurity === undefined) {
        throw new Error(
          "Sleeve or security should not be undefined - should exist in model table data structure"
        );
      }
      if (sleeveOrSecurity.type === "AssetClass") {
        throw new Error("Should be a Sleeve or security, not an Asset Class");
      }

      // remove weight from asset class of fromAssetClassId and
      // add weight to asset class of toAssetClassElementId
      const tableWithUpdatedAssetClassWeights = state.modelTable.map((ac) => {
        const { children, weight: currentWeight, ...rest } = ac;

        // remove weight from asset class of fromAssetClassId and
        if (ac.assetClassId === fromAssetClassId) {
          const difference = new Decimal(currentWeight).sub(
            new Decimal(weight)
          );

          return {
            ...rest,
            weight: difference.toString(),
            children: deepCopyChildren(children),
          };
        }

        // add weight to asset class of toAssetClassElementId
        if (ac.id === toAssetClassElementId) {
          const sum = new Decimal(currentWeight).add(new Decimal(weight));

          return {
            ...rest,
            weight: sum.toString(),
            children: deepCopyChildren(children),
          };
        }

        return {
          ...rest,
          weight: currentWeight,
          children: deepCopyChildren(children),
        };
      });

      const tableWithElementRemoved = deepCopyAndRemoveElementById(
        elementId,
        tableWithUpdatedAssetClassWeights
      );

      return {
        ...state,
        modelTable: tableWithElementRemoved.map((assetClass) => {
          const { children, ...rest } = assetClass;

          return {
            ...rest,
            children:
              assetClass.id === toAssetClassElementId
                ? orderBy(
                    [
                      ...deepCopyChildren(assetClass.children),
                      sleeveOrSecurity,
                    ],
                    [(c) => c.type],
                    ["desc"] // sorts sleeves before securities
                  )
                : deepCopyChildren(assetClass.children),
          };
        }),
      };
    }

    case REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL: {
      const {
        elementId,
        assetClassId,
        elementType,
        weight: removedWeight,
      } = payload;

      if (elementType === "AssetClass") {
        return {
          ...state,
          modelTable: deepCopyAndRemoveElementById(elementId, state.modelTable),
        };
      }

      return {
        ...state,
        modelTable: state.modelTable.map((ac) => {
          const { children, weight, ...rest } = ac;

          if (ac.assetClassId === assetClassId) {
            const difference = new Decimal(weight).sub(
              new Decimal(removedWeight)
            );

            return {
              ...rest,
              weight: difference.toString(), // subtract weight of sleeve or security being removed from weight of asset class of assetClassId
              children: deepCopyAndRemoveChildById(elementId, children),
            };
          }

          return {
            ...rest,
            weight: weight,
            children: deepCopyChildren(children),
          };
        }),
      };
    }

    case SET_SLEEVES_AND_SECURITIES: {
      const { elementId, sleeves, securities } = payload;

      const slvs = sleeves.map((slv) => ({
        id: generateElementId(),
        type: "Sleeve" as Extract<ModelTableRowType, "Sleeve">,
        name: slv.name,
        portfolioPlanId: slv.portfolioPlanId,
        weight: slv.weight,
        isLocked: true,
        expand: true,
        children: [],
      }));

      const secs = securities.map((sec) => ({
        id: generateElementId(),
        type: "Security" as Extract<ModelTableRowType, "Security">,
        ticker: sec.ticker,
        securityId: sec.securityId,
        description: sec.description,
        weight: sec.weight,
        isLocked: true,
      }));

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, ...rest } = assetClass;

          return {
            ...rest,
            children: addSleevesAndSecuritiesToChildInChildren(
              elementId,
              orderBy(slvs, [(s) => s.name], ["asc"]),
              orderBy(secs, [(s) => s.ticker], ["asc"]),
              assetClass.children
            ),
          };
        }),
      };
    }

    case SORT_ASSET_CLASSES_ASC: {
      return {
        ...state,
        modelTable: orderBy(
          state.modelTable.map((ac) => {
            const { children, ...rest } = ac;

            return {
              ...rest,
              children: orderChildrenByNameAsc(children),
            };
          }),
          [(ac) => ac.name],
          ["asc"]
        ),
      };
    }

    case SORT_ASSET_CLASS_WEIGHTS_DESC: {
      return {
        ...state,
        modelTable: orderBy(
          state.modelTable.map((ac) => {
            const { children, ...rest } = ac;

            return {
              ...rest,
              children: orderChildrenByWeightDesc(children),
            };
          }),
          [(ac) => ac.weight],
          ["desc"]
        ),
      };
    }

    case SET_SLEEVE_NAME: {
      // useGetPortfolioPlans does not return the nested plan's name for a plan with a nested plan so
      // when the UI first populates a parent row with that nested plan (the plan will get set and then the nested plan will get recursively set)
      // we need to set a temporary plan name for that nested plan/sleeve, so we set it to TEMPORARY_SLEEVE_NAME. Then, when the useGetPortfolioPlanById
      // hook gets called for that parent row of the nested plan, a use effect will look for the incoming fetched data and call this action to set the name.
      const { elementId, name } = payload;

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { children, ...rest } = assetClass;

          return {
            ...rest,
            children: deepCopyAndUpdateSpecificElement(
              elementId,
              ["name", name],
              assetClass.children
            ),
          };
        }),
      };
    }

    case EXPAND_COLLAPSE_CHILDREN: {
      // elementId is the element that was clicked on to expand or collapse its children - null means expand/collapse all elements: is passed in only by the top row
      // expand is the new state of the children
      // apply determines if expand/collapse should be applied/set to the element and all its children
      const { elementId, expand } = payload;

      return {
        ...state,
        modelTable: state.modelTable.map((assetClass) => {
          const { id, expand: curExpand, children, ...rest } = assetClass;

          const apply = elementId === id || elementId === null;

          return {
            id: id,
            ...rest,
            expand: apply && !assetClass.isInvisible ? expand : curExpand,
            children: deepCopyAndUpdateElementKey(
              elementId,
              ["expand", expand],
              apply,
              assetClass.children
            ),
          };
        }),
      };
    }

    case SET_DEFAULT_ASSET_CLASSES_AND_THEIR_CATEGORIES: {
      return {
        ...state,
        assetCategoriesWithClasses: payload,
      };
    }

    default:
      return assertUnreachable(type);
  }
}

export default reducer;
